import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@app/components/Button/Button';
import {
  listBusinessMembers,
  removeBusinessMember,
  updateBusinessMember,
  MembersListResponse,
  createBusinessInvite,
  resendBusinessInvite,
  cancelBusinessInvite,
} from '@app/services/business';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { Menu, MenuButton, MenuItem, MenuItems, Switch } from '@headlessui/react';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  BusinessMemberInterface,
  BusinessMemberPermissions,
  PermissionKey,
  PERMISSION_LABELS,
  PERMISSION_CATEGORIES,
} from '@app/models/business';
import { useInviteMemberContext } from '../Settings';
import { Tooltip } from '@app/components/Tooltip/Tooltip';
import NoPhoto from '@app/assets/images/no-photo.svg?react';
import { InviteMemberModal } from '@app/components/InviteMemberModal';
import { ConfirmDialog } from '@app/components/ConfirmDialog/ConfirmDialog';
import SearchIcon from '@app/assets/icons/search.svg?react';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';
import UserPlusIcon from '@app/assets/icons/user-plus.svg?react';
import LinkBrokenIcon from '@app/assets/icons/link-broken.svg?react';
import UserProfileIcon from '@app/assets/icons/user-profile-square-alt.svg?react';
import MailIcon from '@app/assets/icons/mail-03.svg?react';
import { usePermissions } from '@app/contexts/PermissionContext';
import { useAppSelector } from '@app/redux/hooks';

const ChevronUpIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

type SortField = 'name' | 'email' | 'jobTitle' | 'status' | 'lastActivity';
type SortDirection = 'asc' | 'desc';

const SortIcon = ({ field, currentField, direction }: { field: SortField; currentField: SortField | null; direction: SortDirection }) => {
  const isActive = field === currentField;
  return (
    <span className="inline-flex flex-col ml-1">
      <ChevronUpIcon
        className={`w-3 h-3 -mb-1 ${isActive && direction === 'asc' ? 'text-base-raisinBlack' : 'text-base-coolGray/40'}`}
      />
      <ChevronDownIcon
        className={`w-3 h-3 ${isActive && direction === 'desc' ? 'text-base-raisinBlack' : 'text-base-coolGray/40'}`}
      />
    </span>
  );
};

const StatusBadge = ({ status }: { status: 'active' | 'pending' }) => {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center text-sm text-base-raisinBlack">
        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-sm text-base-raisinBlack">
      <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
      Request sent
    </span>
  );
};

const MemberAvatar = ({ member }: { member: BusinessMemberInterface }) => {
  if (member.user?.photo) {
    const src = typeof member.user.photo === 'string' ? member.user.photo : (member.user.photo as any)?.url;
    return (
      <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden">
        <img
          src={src}
          alt={`${member.user.firstName} ${member.user.lastName}`}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  return <NoPhoto className="flex-shrink-0 h-10 w-10" />;
};

export const TeamMembers = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showInviteModal, setShowInviteModal } = useInviteMemberContext();
  const { can } = usePermissions();
  const currentUserId = useAppSelector((state) => state.auth.user?.id);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingMember, setEditingMember] = useState<BusinessMemberInterface | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<BusinessMemberPermissions>({});
  const [editingJobTitle, setEditingJobTitle] = useState('');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'default';
    confirmText?: string;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const {
    data: membersData,
    isLoading,
    error,
  } = useQuery<MembersListResponse>({
    queryKey: ['members', currentPage, limit, debouncedSearchTerm, sortField, sortDirection],
    queryFn: () => listBusinessMembers(
      currentPage,
      limit,
      debouncedSearchTerm || undefined,
      sortField || undefined,
      sortField ? sortDirection : undefined
    ),
    placeholderData: keepPreviousData,
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => removeBusinessMember(userId),
    onSuccess: () => {
      toast.success('Member removed successfully');
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove member');
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: { permissions?: BusinessMemberPermissions | null; jobTitle?: string | null };
    }) => updateBusinessMember(userId, data),
    onSuccess: () => {
      toast.success('Member updated successfully');
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update member');
    },
  });

  const createInviteMutation = useMutation({
    mutationFn: (data: {
      email: string;
      jobTitle?: string;
      permissions?: BusinessMemberPermissions;
    }) => createBusinessInvite(data.email, data.jobTitle, data.permissions),
    onSuccess: () => {
      toast.success('Invitation sent successfully');
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setShowInviteModal(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (inviteId: string) => resendBusinessInvite(inviteId),
    onSuccess: () => {
      toast.success('Invitation resent successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to resend invitation');
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId: string) => cancelBusinessInvite(inviteId),
    onSuccess: () => {
      toast.success('Invitation cancelled');
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to cancel invitation');
    },
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSendInvitation = (data: {
    email: string;
    jobTitle: string;
    permissions: BusinessMemberPermissions;
  }) => {
    createInviteMutation.mutate(data);
  };

  const handleRemoveMember = (userId: string, userName: string) => {
    setConfirmDialog({
      open: true,
      title: 'Disconnect Member',
      message: `Are you sure you want to remove ${userName} from this business? They will lose access to all business data.`,
      confirmText: 'Disconnect',
      variant: 'danger',
      onConfirm: () => {
        removeMemberMutation.mutate(userId);
        setConfirmDialog((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const handleCancelInvite = (inviteId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Cancel Invitation',
      message: 'Are you sure you want to cancel this invitation? The invite link will no longer work.',
      confirmText: 'Cancel Invitation',
      variant: 'warning',
      onConfirm: () => {
        cancelInviteMutation.mutate(inviteId);
        setConfirmDialog((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const handleTogglePermission = (key: PermissionKey) => {
    setEditingPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSavePermissions = async () => {
    if (!editingMember) return;

    const hasAnyPermission = Object.values(editingPermissions).some((v) => v === true);
    const newJobTitle = editingJobTitle.trim();

    updateMemberMutation.mutate({
      userId: editingMember.userId!,
      data: {
        permissions: hasAnyPermission ? editingPermissions : null,
        jobTitle: newJobTitle || null,
      },
    });

    setShowPermissionsModal(false);
    setEditingMember(null);
    setEditingPermissions({});
    setEditingJobTitle('');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatLastActivity = (member: BusinessMemberInterface) => {
    if (member.lastActivity) {
      return member.lastActivity;
    }
    return '-';
  };

  if (isLoading && !membersData) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex justify-center items-center h-64"
      >
        <div className="text-base-coolGray">Loading members...</div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex justify-center items-center h-64"
      >
        <div className="text-red-500">Error loading members. Please try again.</div>
      </motion.div>
    );
  }

  const members = membersData?.data || [];
  const totalCount = membersData?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);
  // seatsLeft from API already accounts for pending invites
  const seatsLeft = membersData?.seatsLeft ?? 0;
  const seatLimit = membersData?.seatLimit ?? 0;
  const canInvite = seatsLeft > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 bg-white py-6 -mt-4 -mx-4 px-4 w-[calc(100%+2rem)]">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold text-base-raisinBlack">
            {totalCount} member{totalCount !== 1 ? 's' : ''}
          </h3>
          <Tooltip
            content={`Your plan provides for ${seatLimit} seats for members`}
            direction="down"
          >
            <span className="px-3 py-1 bg-base-ghostWhite rounded-full text-sm text-base-coolGray cursor-help">
              {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''} left
            </span>
          </Tooltip>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-80 pl-3 pr-10 py-2 border-b border-base-ghostWhite text-sm focus:outline-none focus:border-brand-baseBlue bg-transparent"
          />
          <SearchIcon className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-base-coolGray" />
        </div>
      </div>

      <div className="bg-white overflow-hidden">
        {members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-base-ghostWhite rounded-full flex items-center justify-center mb-4">
              <UserPlusIcon className="w-8 h-8 text-base-coolGray" />
            </div>
            <h4 className="text-lg font-semibold text-base-raisinBlack mb-2">No members</h4>
            <p className="text-sm text-base-coolGray text-center mb-6 max-w-sm">
              Add your business members to effectively manage the platform
            </p>
            {can('editTeamMembers') && (
              <Button
                onClick={() => setShowInviteModal(true)}
                disabled={!canInvite}
              >
                + Invite member
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-base-ghostWhite">
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-base-coolGray uppercase tracking-wider cursor-pointer hover:text-base-raisinBlack"
                    onClick={() => handleSort('name')}
                  >
                    <span className="inline-flex items-center">
                      Member
                      <SortIcon field="name" currentField={sortField} direction={sortDirection} />
                    </span>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-base-coolGray uppercase tracking-wider cursor-pointer hover:text-base-raisinBlack"
                    onClick={() => handleSort('email')}
                  >
                    <span className="inline-flex items-center">
                      Email
                      <SortIcon field="email" currentField={sortField} direction={sortDirection} />
                    </span>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-base-coolGray uppercase tracking-wider cursor-pointer hover:text-base-raisinBlack"
                    onClick={() => handleSort('jobTitle')}
                  >
                    <span className="inline-flex items-center">
                      Job Title
                      <SortIcon field="jobTitle" currentField={sortField} direction={sortDirection} />
                    </span>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-base-coolGray uppercase tracking-wider cursor-pointer hover:text-base-raisinBlack"
                    onClick={() => handleSort('status')}
                  >
                    <span className="inline-flex items-center">
                      Status
                      <SortIcon field="status" currentField={sortField} direction={sortDirection} />
                    </span>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-base-coolGray uppercase tracking-wider cursor-pointer hover:text-base-raisinBlack"
                    onClick={() => handleSort('lastActivity')}
                  >
                    <span className="inline-flex items-center">
                      Last Activity
                      <SortIcon field="lastActivity" currentField={sortField} direction={sortDirection} />
                    </span>
                  </th>
                  <th className="px-6 py-4 w-12">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-ghostWhite">
                {members.map((member: BusinessMemberInterface) => (
                  <tr
                    key={member.id}
                    className={`hover:bg-gray-50 ${member.userId ? 'cursor-pointer' : ''}`}
                    onClick={() => member.userId && navigate(`/settings/members/${member.userId}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MemberAvatar member={member} />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-base-raisinBlack">
                            {member.user?.firstName && member.user?.lastName
                              ? `${member.user.firstName} ${member.user.lastName}`
                              : member.user?.email || member.email || 'Pending invitation'}
                          </div>
                          {member.isOwner && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              Owner
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-base-coolGray">
                        {member.user?.email || member.email}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-base-coolGray">
                        {member.jobTitle || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={member.userId === null ? 'pending' : 'active'} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-base-coolGray">
                      {formatLastActivity(member)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                      {member.isOwner || member.userId === currentUserId ? null : (
                        <Menu>
                          <MenuButton className="text-base-coolGray hover:text-base-raisinBlack p-1">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </MenuButton>
                          <MenuItems
                            transition
                            anchor="bottom end"
                            className="w-52 p-1 bg-white shadow-lg rounded-lg border border-base-ghostWhite transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 z-10"
                          >
                            {member.userId && (
                              <MenuItem>
                                <button
                                  onClick={() => navigate(`/settings/members/${member.userId}`)}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-base-raisinBlack hover:bg-base-ghostWhite rounded-md"
                                >
                                  <UserProfileIcon className="w-5 h-5" />
                                  Open profile
                                </button>
                              </MenuItem>
                            )}
                            {can('editTeamMembers') && member.invite && (
                              <MenuItem>
                                <button
                                  onClick={() => {
                                    resendInviteMutation.mutate(member.invite!.id);
                                  }}
                                  disabled={resendInviteMutation.isPending}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-base-raisinBlack hover:bg-base-ghostWhite rounded-md disabled:opacity-50"
                                >
                                  <MailIcon className="w-5 h-5" />
                                  {resendInviteMutation.isPending ? 'Sending...' : 'Send invitation again'}
                                </button>
                              </MenuItem>
                            )}
                            {can('editTeamMembers') && (
                              <MenuItem>
                                <button
                                  onClick={() => {
                                    if (member.invite) {
                                      handleCancelInvite(member.invite.id);
                                    } else {
                                      handleRemoveMember(
                                        member.userId!,
                                        `${member.user?.firstName} ${member.user?.lastName}`
                                      );
                                    }
                                  }}
                                  disabled={cancelInviteMutation.isPending || removeMemberMutation.isPending}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                                >
                                  <LinkBrokenIcon className="w-5 h-5" />
                                  {member.invite ? 'Cancel invitation' : 'Disconnect member'}
                                </button>
                              </MenuItem>
                            )}
                          </MenuItems>
                        </Menu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-base-ghostWhite flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-base-coolGray">Page</span>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    handlePageChange(page);
                  }
                }}
                min={1}
                max={totalPages}
                className="w-12 px-2 py-1 border border-base-ghostWhite rounded text-sm text-center focus:outline-none focus:ring-1 focus:ring-brand-baseBlue"
              />
              <span className="text-sm text-base-coolGray">of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 text-base-coolGray hover:text-base-raisinBlack disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-1 text-sm text-brand-baseBlue hover:text-brand-baseBlue/80 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next page
                <ChevronRightIcon className="w-5 h-5 ml-1" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-base-coolGray">Show</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-base-ghostWhite rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-baseBlue"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-base-coolGray">per page</span>
            </div>
          </div>
        )}
      </div>

      <InviteMemberModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSubmit={handleSendInvitation}
        canInvite={canInvite}
        existingEmails={members.map((m) => m.user?.email || m.email || '')}
        isLoading={createInviteMutation.isPending}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        isLoading={removeMemberMutation.isPending || cancelInviteMutation.isPending}
      />

      {showPermissionsModal && editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-base-raisinBlack">Edit Permissions</h3>
                  <p className="text-sm text-base-coolGray">
                    {editingMember.user?.firstName} {editingMember.user?.lastName}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setEditingMember(null);
                    setEditingPermissions({});
                    setEditingJobTitle('');
                  }}
                  className="text-base-coolGray hover:text-base-raisinBlack"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-base-raisinBlack mb-2">
                  Job Title
                </label>
                <input
                  type="text"
                  value={editingJobTitle}
                  onChange={(e) => setEditingJobTitle(e.target.value)}
                  placeholder="e.g., Marketing Manager"
                  className="w-full px-4 py-2 border border-base-ghostWhite rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-baseBlue"
                />
              </div>

              <div className="max-h-96 overflow-y-auto">
                {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => (
                  <div key={categoryKey} className="mb-6">
                    <h4 className="text-xs font-semibold text-base-coolGray mb-3 uppercase tracking-wider">
                      {category.label}
                    </h4>
                    <div className="space-y-3">
                      {category.permissions.map((permKey) => (
                        <label
                          key={permKey}
                          className="flex items-center justify-between cursor-pointer"
                        >
                          <span className="text-sm text-base-raisinBlack">
                            {PERMISSION_LABELS[permKey]}
                          </span>
                          <Switch
                            checked={editingPermissions[permKey] === true}
                            onChange={() => handleTogglePermission(permKey)}
                            className="group relative flex h-6 w-12 cursor-pointer rounded-full bg-gray-200 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-brand-baseBlue flex-shrink-0"
                          >
                            <span
                              aria-hidden="true"
                              className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
                            />
                          </Switch>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-base-ghostWhite">
                <Button
                  kind="secondary"
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setEditingMember(null);
                    setEditingPermissions({});
                    setEditingJobTitle('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePermissions}
                  disabled={updateMemberMutation.isPending}
                >
                  {updateMemberMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};
